/* eslint-disable react-hooks/exhaustive-deps */
import { Box, Grid, MenuItem, Typography } from "@mui/material";
import { FormTextField, GridCardContent } from "components/atoms";
import { FormContainer, FormContent } from "components/molecules";
import { FormikHelpers, useFormik } from "formik";
import { DialogHelper } from "helpers";
import { useRequest } from "hooks/useRequest";
import {
  EStatus,
  ICreateWarehouseRequest,
  IGetByIdRequest,
  IUpdateWarehouseRequest,
  IWarehouse,
} from "model";
import BasePage from "pages/base";
import { useEffect, useState } from "react";
import { useHistory, useLocation, useParams } from "react-router-dom";
import { WarehouseActions } from "redux/actions";
import {
  useAppDispatch,
  useAppSelector,
} from "redux/configurations/reduxHooks";
import { WarehouseApiService } from "services";
import { I18nTranslator, translations } from "utils/i18n";
import * as yup from "yup";

const validateSchema = yup.object().shape({
  warehouseCode: yup.string().required(translations.yupError.required),
  description: yup.string().required(translations.yupError.required),
});

const WarehouseDetail = () => {
  const history = useHistory();
  const params = useParams<{ id: string }>();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const isAdd = location.pathname.includes("new");
  const action = useAppSelector((state) => state.WarehouseReducer.action);
  const [request] = useState({
    id: params.id,
  });
  const { loading, data } = useRequest<IGetByIdRequest, IWarehouse>(
    request,
    WarehouseApiService.getWarehouseById,
    [],
    { holdRequest: isAdd }
  );

  const createWarehouseInitialValues: ICreateWarehouseRequest = {
    warehouseCode: "",
    description: "",
    status: EStatus.ACTIVE,
  };

  const updateWarehouseInitialValues: IUpdateWarehouseRequest = {
    ...(data as IWarehouse),
    status: data?.status ? EStatus.ACTIVE : EStatus.IN_ACTIVE,
  };

  const initialValues = isAdd
    ? createWarehouseInitialValues
    : updateWarehouseInitialValues;

  const formik = useFormik({
    initialValues,
    enableReinitialize: true,
    validationSchema: validateSchema,
    onSubmit: (
      values: ICreateWarehouseRequest | IUpdateWarehouseRequest,
      {
        validateForm,
      }: FormikHelpers<ICreateWarehouseRequest | IUpdateWarehouseRequest>
    ) => {
      validateForm().then(() => {
        const createRequest: ICreateWarehouseRequest = {
          ...values,
          status: values.status === EStatus.ACTIVE,
        };
        if (isAdd) {
          dispatch(WarehouseActions.createWarehouse.request(createRequest));
        } else {
          const updateRequest: IUpdateWarehouseRequest = {
            ...(values as IWarehouse),
            status: values.status === EStatus.ACTIVE,
          };
          dispatch(WarehouseActions.updateWarehouse.request(updateRequest));
        }
      });
    },
  });

  const createOrUpdateOrDeleteWarehouseSuccess =
    action.includes(WarehouseActions.createWarehouse.successName) ||
    action.includes(WarehouseActions.updateWarehouse.successName) ||
    action.includes(WarehouseActions.deleteWarehouse.successName);

  const getCardTitle = () => {
    return isAdd
      ? translations.masterData.addWarehouse
      : translations.masterData.editWarehouse;
  };

  const handleDeleteItem = () => {
    DialogHelper.openConfirmDeleteDialog({
      itemName: data?.description,
      onDelete: () => _deleteWarehouse(data?.id!),
    });
  };
  const _clearAction = () => {
    dispatch(WarehouseActions.clearAction.request());
  };
  const _deleteWarehouse = (id: string) => {
    dispatch(
      WarehouseActions.deleteWarehouse.request({
        id: id,
      })
    );
  };

  useEffect(() => {
    if (createOrUpdateOrDeleteWarehouseSuccess) {
      _clearAction();
      history.go(-1);
    }
  }, [action]);

  return (
    <BasePage loading={loading || action.includes("_REQUEST")}>
      <GridCardContent>
        <Box sx={{ p: 2, width: "100%" }}>
          <FormContainer
            formik={formik}
            loading={loading || action.includes("_REQUEST")}
            isAdd={isAdd}
            onDelete={handleDeleteItem}
            title={I18nTranslator(getCardTitle())}
          >
            <FormGeneralInformation />
          </FormContainer>
        </Box>
      </GridCardContent>
    </BasePage>
  );
};

const FormGeneralInformation = () => {
  return (
    <FormContent
      title={I18nTranslator(translations.masterData.generaInformation)}
    >
      <Grid xs={6} item>
        <FormTextField
          required
          label={I18nTranslator(translations.masterData.warehouseCode)}
          placeholder={I18nTranslator(translations.placeholder.warehouseCode)}
          id="warehouseCode"
        />
      </Grid>
      <Grid xs={6} item>
        <FormTextField
          required
          label={I18nTranslator(translations.masterData.warehouseName)}
          placeholder={I18nTranslator(translations.placeholder.warehouseName)}
          id="description"
        />
      </Grid>
      <Grid xs={6} item>
        <FormTextField
          label={I18nTranslator(translations.masterData.status)}
          select
          id="status"
        >
          <MenuItem value={EStatus.ACTIVE}>
            {I18nTranslator(translations.masterData.active)}
          </MenuItem>
          <MenuItem value={EStatus.IN_ACTIVE}>
            {I18nTranslator(translations.masterData.inactive)}
          </MenuItem>
        </FormTextField>
      </Grid>
    </FormContent>
  );
};

export default WarehouseDetail;
